import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Save, RotateCcw } from 'lucide-react';

/**
 * Edit the copy that wraps the weekly release digest.
 *
 * The subject, heading, intro, call to action and footer used to be written
 * into email_service.py, so changing one sentence meant a build and a
 * production deploy. They are editable rows now (migration 043).
 *
 * The release LIST is deliberately not editable. It is generated from
 * project_events — data, not copy — and an editable loop is how you end up
 * maintaining a template language.
 *
 * The preview renders server-side through the same call the real send makes,
 * so what is shown is the email, not a lookalike built in the browser.
 */
const FIELDS = [
  { key: 'subject', label: 'Subject line', type: 'text',
    hint: 'What subscribers see in their inbox. Required.' },
  { key: 'heading', label: 'Heading', type: 'text',
    hint: 'The large heading at the top of the email.' },
  { key: 'intro', label: 'Intro', type: 'textarea',
    hint: 'One or two sentences above the list of releases.' },
  { key: 'cta_text', label: 'Button text', type: 'text',
    hint: 'The link below the list. Leave both this and the link empty to drop it.' },
  { key: 'cta_url', label: 'Button link', type: 'text',
    hint: 'Must be a full http:// or https:// address.' },
  { key: 'footer_note', label: 'Footer note', type: 'textarea',
    hint: 'Shown above the unsubscribe link, which is always added.' },
];

const DigestTemplateEditor = ({ apiBase, headers }) => {
  const [form, setForm] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [maxLengths, setMaxLengths] = useState({});
  const [meta, setMeta] = useState({});
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/admin/email/template`, { headers: headers() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setForm(d.template);
      setDefaults(d.defaults || {});
      setMaxLengths(d.max_lengths || {});
      setMeta(d.meta || {});
      setDirty(false);
    } catch (e) {
      setStatus('Could not load the digest copy.');
    }
  }, [apiBase, headers]);

  useEffect(() => { load(); }, [load]);

  const change = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    setPreview(null); // a stale preview beside edited fields is worse than none
  };

  const save = async () => {
    setBusy(true);
    setStatus('');
    try {
      const r = await fetch(`${apiBase}/admin/email/template`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(form),
      });
      const d = await r.json();
      if (r.ok) {
        setForm(d.template);
        setDirty(false);
        setStatus('Saved. The next digest will use this copy.');
        load();
      } else {
        setStatus(d.detail || 'Could not save.');
      }
    } catch (e) {
      setStatus('Could not save.');
    }
    setBusy(false);
  };

  const showPreview = async () => {
    setBusy(true);
    setStatus('');
    try {
      const r = await fetch(`${apiBase}/admin/email/template/preview`, {
        method: 'POST', headers: headers(), body: JSON.stringify(form),
      });
      const d = await r.json();
      if (r.ok) setPreview(d);
      else setStatus(d.detail || 'Could not render the preview.');
    } catch (e) {
      setStatus('Could not render the preview.');
    }
    setBusy(false);
  };

  const restore = () => {
    setForm({ ...defaults });
    setDirty(true);
    setPreview(null);
    setStatus('Original wording restored. Not saved yet.');
  };

  if (!form) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
        <p className="text-gray-500 text-sm">Loading digest copy…</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-lg font-semibold text-gray-900">Digest content</h2>
        {dirty && (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap">
            Unsaved changes
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        The wording around the releases. The list of releases itself is generated
        from the calendar and is not editable.
        {meta.updated_at && (
          <> Last edited {new Date(meta.updated_at).toLocaleString()}
            {meta.updated_by ? ` by ${meta.updated_by}` : ''}.</>
        )}
      </p>

      <div className="space-y-4">
        {FIELDS.map(({ key, label, type, hint }) => {
          const limit = maxLengths[key];
          const value = form[key] ?? '';
          const over = limit && value.length > limit;
          return (
            <div key={key}>
              <div className="flex items-baseline justify-between">
                <label htmlFor={`digest-${key}`} className="block text-sm font-medium text-gray-700">
                  {label}
                </label>
                {limit && (
                  <span className={`text-xs ${over ? 'text-red-600' : 'text-gray-400'}`}>
                    {value.length}/{limit}
                  </span>
                )}
              </div>
              {type === 'textarea' ? (
                <textarea
                  id={`digest-${key}`}
                  rows={2}
                  value={value}
                  onChange={(e) => change(key, e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-videbits-500 focus:ring-videbits-500"
                />
              ) : (
                <input
                  id={`digest-${key}`}
                  type="text"
                  value={value}
                  onChange={(e) => change(key, e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-videbits-500 focus:ring-videbits-500"
                />
              )}
              <p className="text-xs text-gray-400 mt-1">{hint}</p>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-5">
        <button
          onClick={save}
          disabled={busy || !dirty}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-videbits-600 text-white text-sm font-medium hover:bg-videbits-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> Save
        </button>
        <button
          onClick={showPreview}
          disabled={busy}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Eye className="w-4 h-4" /> Preview
        </button>
        <button
          onClick={restore}
          disabled={busy}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
        >
          <RotateCcw className="w-4 h-4" /> Restore original wording
        </button>
      </div>

      {status && <p className="text-sm text-gray-600 mt-3">{status}</p>}

      {preview && (
        <div className="mt-5 border-t pt-4">
          <div className="text-sm text-gray-500 mb-2">
            Subject: <span className="font-medium text-gray-900">{preview.subject}</span>
          </div>
          {/* Sandboxed iframe: it renders the real email markup faithfully and
              keeps it out of the admin page's own document. */}
          <iframe
            title="Digest preview"
            sandbox=""
            srcDoc={preview.html}
            className="w-full h-96 border border-gray-200 rounded-lg bg-white"
          />
          <p className="text-xs text-gray-400 mt-2">
            Releases shown are examples. The real send uses the upcoming calendar.
          </p>
        </div>
      )}
    </div>
  );
};

export default DigestTemplateEditor;
